import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useTranslations } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * Detects if we're on a mobile device (Android/iOS).
 * Used to apply mobile-specific keyboard handling.
 */
function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

interface SelectSearchContextValue {
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  open: boolean;
  setOpen: (open: boolean) => void;
  // For Android keyboard handling - track when search input is focused
  onSearchInputFocus: () => void;
  onSearchInputBlur: () => void;
  // Forced side when reopening after keyboard opens
  forcedSide: 'top' | 'bottom' | undefined;
  // Ref to refocus search input after reopen
  searchInputRef: React.RefObject<HTMLInputElement | null>;
}

const SelectSearchContext = React.createContext<SelectSearchContextValue | null>(null);

function useSelectSearchContext() {
  return React.useContext(SelectSearchContext);
}

export function useSelectSearch() {
  const context = useSelectSearchContext();
  if (!context) {
    throw new Error('useSelectSearch must be used within a Select component');
  }
  return context;
}

function isPrintableKey(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key.length !== 1) {
    return false;
  }
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return false;
  }
  return true;
}

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join(' ');
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return extractText(node.props.children ?? '');
  }
  return '';
}

function filterSelectChildren(
  children: React.ReactNode,
  searchTerm: string
): { nodes: React.ReactNode; hasMatch: boolean } {
  if (searchTerm.length === 0) {
    return { nodes: children, hasMatch: true };
  }

  let hasMatch = false;

  const filtered = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    if (child.type === SelectItem) {
      const itemProps = child.props as React.ComponentProps<typeof SelectItem>;
      const rawContent = itemProps.searchText ?? extractText(itemProps.children);
      const textValue = rawContent.toLowerCase();
      if (textValue.includes(searchTerm)) {
        hasMatch = true;
        return child;
      }
      return null;
    }

    const childProps = child.props as { children?: React.ReactNode };
    if (childProps.children) {
      const { nodes: nested, hasMatch: nestedMatch } = filterSelectChildren(
        childProps.children,
        searchTerm
      );
      if (nestedMatch) {
        hasMatch = true;
        return React.cloneElement(child, undefined, nested as React.ReactNode);
      }
      return null;
    }

    return child;
  });

  return { nodes: filtered, hasMatch };
}

function Select({
  children,
  open: openProp,
  defaultOpen,
  onOpenChange,
  onValueChange,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [internalOpen, setInternalOpen] = React.useState<boolean>(defaultOpen ?? false);
  // Force dropdown to open on top when keyboard opens on mobile
  const [forcedSide, setForcedSide] = React.useState<'top' | 'bottom' | undefined>(undefined);

  const isControlled = openProp !== undefined;
  const currentOpen = isControlled ? (openProp as boolean) : internalOpen;

  // Track when search input is focused (for Android keyboard detection)
  const searchInputFocusedRef = React.useRef(false);
  const searchInputFocusTimeRef = React.useRef<number>(0);
  // Ref to access search input element
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (isControlled) {
      setInternalOpen(openProp as boolean);
    }
  }, [isControlled, openProp]);

  React.useEffect(() => {
    if (!currentOpen) {
      setSearchTerm('');
      // Reset forced side when dropdown closes
      setForcedSide(undefined);
    }
  }, [currentOpen]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      // Detect if this close was triggered by keyboard opening on Android
      if (!open && isMobileDevice()) {
        const timeSinceFocus = Date.now() - searchInputFocusTimeRef.current;
        const wasSearchInputFocused = searchInputFocusedRef.current;

        // If search input was recently focused (within 800ms), this is likely
        // a keyboard-triggered close from Radix's window resize/blur listener
        if (wasSearchInputFocused && timeSinceFocus < 800) {
          // BLOCK THE CLOSE - don't let Radix close the dropdown
          // Also force the dropdown to open on top to avoid keyboard overlap
          setForcedSide('top');
          // Don't call onOpenChange or update state - keep dropdown open
          return;
        }
      }

      // Normal open/close handling
      if (open) {
        // Reset forced side when opening normally
        setForcedSide(undefined);
      }

      if (!isControlled) {
        setInternalOpen(open);
      }
      if (!open) {
        setSearchTerm('');
      }
      onOpenChange?.(open);
    },
    [isControlled, onOpenChange]
  );

  const handleValueChange = React.useCallback(
    (value: string) => {
      setSearchTerm('');
      setForcedSide(undefined);
      onValueChange?.(value);
    },
    [onValueChange]
  );

  const setOpen = React.useCallback(
    (open: boolean) => {
      if (open === currentOpen) {
        return;
      }
      if (!isControlled) {
        setInternalOpen(open);
      }
      if (!open) {
        setSearchTerm('');
        setForcedSide(undefined);
      }
      onOpenChange?.(open);
    },
    [currentOpen, isControlled, onOpenChange]
  );

  const onSearchInputFocus = React.useCallback(() => {
    searchInputFocusedRef.current = true;
    searchInputFocusTimeRef.current = Date.now();
    // When search input is focused on mobile, force dropdown to top
    // to prepare for keyboard opening
    if (isMobileDevice()) {
      setForcedSide('top');
    }
  }, []);

  const onSearchInputBlur = React.useCallback(() => {
    // Delay resetting to allow for keyboard close animation
    setTimeout(() => {
      searchInputFocusedRef.current = false;
    }, 300);
  }, []);

  const contextValue = React.useMemo<SelectSearchContextValue>(
    () => ({
      searchTerm,
      setSearchTerm,
      open: currentOpen,
      setOpen,
      onSearchInputFocus,
      onSearchInputBlur,
      forcedSide,
      searchInputRef,
    }),
    [currentOpen, searchTerm, setOpen, onSearchInputFocus, onSearchInputBlur, forcedSide]
  );

  return (
    <SelectSearchContext.Provider value={contextValue}>
      <SelectPrimitive.Root
        data-slot="select"
        open={isControlled ? (openProp as boolean) : internalOpen}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        onValueChange={handleValueChange}
        {...props}
      >
        {children}
      </SelectPrimitive.Root>
    </SelectSearchContext.Provider>
  );
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ children, ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  const context = useSelectSearchContext();
  const rawSearchTerm = context?.searchTerm ?? '';
  const overrideValue = context?.open && rawSearchTerm.length > 0 ? rawSearchTerm : undefined;

  if (overrideValue !== undefined) {
    return (
      <SelectPrimitive.Value data-slot="select-value" {...props}>
        {overrideValue}
      </SelectPrimitive.Value>
    );
  }

  if (children !== undefined) {
    return (
      <SelectPrimitive.Value data-slot="select-value" {...props}>
        {children}
      </SelectPrimitive.Value>
    );
  }

  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = 'default',
  children,
  variant = 'default',
  showIcon = true,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default';
  variant?: 'default' | 'formField';
  showIcon?: boolean;
}) {
  const context = useSelectSearchContext();

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!context) {
        onKeyDown?.(event);
        return;
      }

      if (event.key === 'Backspace') {
        const hasTerm = context.searchTerm.length > 0;
        if (!context.open) {
          context.setOpen(true);
        }
        if (hasTerm) {
          event.preventDefault();
          context.setSearchTerm((prev) => prev.slice(0, -1));
        }
        onKeyDown?.(event);
        return;
      }

      if (isPrintableKey(event)) {
        event.preventDefault();
        const nextChar = event.key;
        if (!context.open) {
          context.setOpen(true);
          context.setSearchTerm(nextChar);
        } else {
          context.setSearchTerm((prev) => prev + nextChar);
        }
        return;
      }

      if (event.key === 'Escape' && context.open) {
        context.setSearchTerm('');
        onKeyDown?.(event);
        return;
      }

      onKeyDown?.(event);
    },
    [context, onKeyDown]
  );

  const defaultClasses =
    "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";
  const formFieldClasses = 'flex w-full items-center justify-between';
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(variant === 'formField' ? formFieldClasses : defaultClasses, className)}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
      {showIcon && (
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon className="size-4 opacity-50" />
        </SelectPrimitive.Icon>
      )}
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  side,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  const context = useSelectSearchContext();
  const tCommon = useTranslations('common');
  const rawSearchTerm = context?.searchTerm ?? '';
  const normalizedSearchTerm = rawSearchTerm.trim().toLowerCase();
  // Use context's searchInputRef if available (for refocusing after keyboard reopen)
  const localInputRef = React.useRef<HTMLInputElement>(null);

  // Sync local ref with context ref
  React.useEffect(() => {
    if (context?.searchInputRef) {
      (context.searchInputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        localInputRef.current;
    }
  });

  // Determine side - use forcedSide from context if available, otherwise use prop
  const effectiveSide = context?.forcedSide ?? side;

  const { nodes: filteredChildren, hasMatch } = React.useMemo(() => {
    if (!context) {
      return { nodes: children, hasMatch: true };
    }
    return filterSelectChildren(children, normalizedSearchTerm);
  }, [children, context, normalizedSearchTerm]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!context) {
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        context.setSearchTerm((prev) => prev.slice(0, -1));
        return;
      }

      if (event.key === 'Escape') {
        context.setSearchTerm('');
        return;
      }

      if (event.key === 'Tab') {
        context.setSearchTerm('');
        return;
      }

      if (isPrintableKey(event)) {
        event.preventDefault();
        const nextChar = event.key;
        context.setSearchTerm((prev) => prev + nextChar);
      }
    },
    [context]
  );

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!context) {
        return;
      }
      context.setSearchTerm(event.target.value);
    },
    [context]
  );

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!context) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        context.setSearchTerm('');
        context.setOpen(false);
        return;
      }

      if (event.key === 'Tab') {
        context.setSearchTerm('');
        return;
      }

      // Let arrow keys, enter, etc. bubble up for native select navigation
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Home', 'End'].includes(event.key)) {
        return;
      }
    },
    [context]
  );

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'group/select bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 flex max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) flex-col overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        side={effectiveSide}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {/* Search input for mobile keyboard support - order-first on bottom, order-last on top */}
        {/* onPointerDown/onTouchStart stopPropagation prevents Radix dismissable layer from closing */}
        <div
          className="bg-popover sticky top-0 z-10 order-first px-1 pt-1 pb-0.5 group-data-[side=top]/select:sticky group-data-[side=top]/select:top-auto group-data-[side=top]/select:bottom-0 group-data-[side=top]/select:order-last group-data-[side=top]/select:pt-0.5 group-data-[side=top]/select:pb-1 md:hidden"
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            ref={localInputRef}
            type="text"
            value={rawSearchTerm}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={context?.onSearchInputFocus}
            onBlur={context?.onSearchInputBlur}
            placeholder={tCommon('search')}
            className="border-input bg-background focus:border-ring focus:ring-ring/50 w-full rounded-sm border px-2 py-1.5 text-base outline-none focus:ring-1"
          />
        </div>
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1'
          )}
        >
          {filteredChildren}
          {normalizedSearchTerm && !hasMatch ? (
            <div className="px-3 py-2 text-sm text-[#666666]">{tCommon('noResults')}</div>
          ) : null}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  searchText: _searchText,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & { searchText?: string }) {
  void _searchText;
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

// ============================================================================
// Virtualized Select Components for Large Lists
// ============================================================================

export interface VirtualizedSelectOption {
  value: string;
  label: string;
  searchText?: string;
  /** Optional suffix displayed in muted style after the label (e.g., "(დაკავებულია 3 დღე)") */
  labelSuffix?: string;
}

interface VirtualizedSelectContentProps
  extends Omit<React.ComponentProps<typeof SelectPrimitive.Content>, 'children'> {
  options: VirtualizedSelectOption[];
  itemClassName?: string;
  /** Estimated height of each item in pixels (default: 44) */
  estimatedItemHeight?: number;
}

function VirtualizedSelectContent({
  className,
  options,
  itemClassName,
  estimatedItemHeight = 44,
  position = 'popper',
  side,
  ...props
}: VirtualizedSelectContentProps) {
  const context = useSelectSearchContext();
  const tCommon = useTranslations('common');
  const rawSearchTerm = context?.searchTerm ?? '';
  const normalizedSearchTerm = rawSearchTerm.trim().toLowerCase();
  // Use context's searchInputRef if available (for refocusing after keyboard reopen)
  const localInputRef = React.useRef<HTMLInputElement>(null);
  // Use state for scroll element so virtualizer re-initializes when element becomes available
  const [scrollElement, setScrollElement] = React.useState<HTMLDivElement | null>(null);

  // Sync local ref with context ref
  React.useEffect(() => {
    if (context?.searchInputRef) {
      (context.searchInputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        localInputRef.current;
    }
  });

  // Determine side - use forcedSide from context if available, otherwise use prop
  const effectiveSide = context?.forcedSide ?? side;

  // Track scroll position for showing/hiding scroll buttons
  const [canScrollUp, setCanScrollUp] = React.useState(false);
  const [canScrollDown, setCanScrollDown] = React.useState(true);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (normalizedSearchTerm.length === 0) {
      return options;
    }
    return options.filter((option) => {
      const searchTarget = (option.searchText ?? option.label).toLowerCase();
      return searchTarget.includes(normalizedSearchTerm);
    });
  }, [options, normalizedSearchTerm]);

  const hasMatch = filteredOptions.length > 0;

  // Setup virtualizer - uses scrollElement state so it re-initializes when element is attached
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimatedItemHeight,
    overscan: 5,
  });

  // Update scroll button visibility
  const updateScrollButtons = React.useCallback(() => {
    if (!scrollElement) return;
    setCanScrollUp(scrollElement.scrollTop > 0);
    setCanScrollDown(
      scrollElement.scrollTop + scrollElement.clientHeight < scrollElement.scrollHeight - 1
    );
  }, [scrollElement]);

  // Check scroll buttons after virtualizer updates
  React.useEffect(() => {
    // Small delay to let virtualizer render
    const timer = setTimeout(updateScrollButtons, 50);
    return () => clearTimeout(timer);
  }, [filteredOptions.length, updateScrollButtons]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!context) {
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        context.setSearchTerm((prev) => prev.slice(0, -1));
        return;
      }

      if (event.key === 'Escape') {
        context.setSearchTerm('');
        return;
      }

      if (event.key === 'Tab') {
        context.setSearchTerm('');
        return;
      }

      if (isPrintableKey(event)) {
        event.preventDefault();
        const nextChar = event.key;
        context.setSearchTerm((prev) => prev + nextChar);
      }
    },
    [context]
  );

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!context) {
        return;
      }
      context.setSearchTerm(event.target.value);
    },
    [context]
  );

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!context) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        context.setSearchTerm('');
        context.setOpen(false);
        return;
      }

      if (event.key === 'Tab') {
        context.setSearchTerm('');
        return;
      }

      // Let arrow keys, enter, etc. bubble up for native select navigation
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Home', 'End'].includes(event.key)) {
        return;
      }
    },
    [context]
  );

  const handleScrollUp = React.useCallback(() => {
    if (!scrollElement) return;
    scrollElement.scrollBy({ top: -estimatedItemHeight * 3, behavior: 'smooth' });
  }, [estimatedItemHeight, scrollElement]);

  const handleScrollDown = React.useCallback(() => {
    if (!scrollElement) return;
    scrollElement.scrollBy({ top: estimatedItemHeight * 3, behavior: 'smooth' });
  }, [estimatedItemHeight, scrollElement]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  // Calculate a reasonable max height (roughly 8 items or available space)
  const maxViewportHeight = Math.min(estimatedItemHeight * 8, totalHeight);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'group/select bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 flex min-w-[8rem] origin-(--radix-select-content-transform-origin) flex-col overflow-hidden rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        side={effectiveSide}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {/* Search input for mobile keyboard support - order-first on bottom, order-last on top */}
        {/* onPointerDown/onTouchStart stopPropagation prevents Radix dismissable layer from closing */}
        <div
          className="bg-popover sticky top-0 z-10 order-first px-1 pt-1 pb-0.5 group-data-[side=top]/select:sticky group-data-[side=top]/select:top-auto group-data-[side=top]/select:bottom-0 group-data-[side=top]/select:order-last group-data-[side=top]/select:pt-0.5 group-data-[side=top]/select:pb-1 md:hidden"
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            ref={localInputRef}
            type="text"
            value={rawSearchTerm}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={context?.onSearchInputFocus}
            onBlur={context?.onSearchInputBlur}
            placeholder={tCommon('search')}
            className="border-input bg-background focus:border-ring focus:ring-ring/50 w-full rounded-sm border px-2 py-1.5 text-base outline-none focus:ring-1"
          />
        </div>

        {/* Scroll up button */}
        {canScrollUp && (
          <button
            type="button"
            className="flex w-full cursor-default items-center justify-center py-1"
            onClick={handleScrollUp}
            aria-hidden="true"
          >
            <ChevronUpIcon className="size-4" />
          </button>
        )}

        {/* Virtualized viewport */}
        <div
          ref={setScrollElement}
          onScroll={updateScrollButtons}
          className="w-full overflow-y-auto p-1"
          style={{
            maxHeight: maxViewportHeight,
            minWidth: position === 'popper' ? 'var(--radix-select-trigger-width)' : undefined,
          }}
        >
          {hasMatch ? (
            <div
              style={{
                height: totalHeight,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualItems.map((virtualItem) => {
                const option = filteredOptions[virtualItem.index];
                return (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    data-slot="select-item"
                    className={cn(
                      "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground absolute top-0 left-0 flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                      itemClassName
                    )}
                    style={{
                      transform: `translateY(${virtualItem.start}px)`,
                      height: virtualItem.size,
                    }}
                  >
                    <span className="absolute right-2 flex size-3.5 items-center justify-center">
                      <SelectPrimitive.ItemIndicator>
                        <CheckIcon className="size-4" />
                      </SelectPrimitive.ItemIndicator>
                    </span>
                    <SelectPrimitive.ItemText>
                      {option.label}
                      {option.labelSuffix && (
                        <span className="ml-2 inline-flex items-center rounded-lg bg-[#fff8e6] px-2.5 py-1 text-sm font-medium text-[#946800]">
                          {option.labelSuffix}
                        </span>
                      )}
                    </SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                );
              })}
            </div>
          ) : (
            <div className="px-3 py-2 text-sm text-[#666666]">{tCommon('noResults')}</div>
          )}
        </div>

        {/* Scroll down button */}
        {canScrollDown && hasMatch && filteredOptions.length > 8 && (
          <button
            type="button"
            className="flex w-full cursor-default items-center justify-center py-1"
            onClick={handleScrollDown}
            aria-hidden="true"
          >
            <ChevronDownIcon className="size-4" />
          </button>
        )}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  VirtualizedSelectContent,
};
